import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  DashboardMetrics,
  SystemHealth,
  ActivityLog,
  AdminUser,
  CreateAdminUserRequest,
  UpdateAdminUserRequest,
  CandidateProfile,
  UpdateSystemSettingsRequest,
  ExportRequest,
  ExportResponse,
} from '../../services/unified/types';

/**
 * Query hook for getting dashboard metrics
 */
export const useDashboardMetrics = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'dashboard', 'metrics'],
    queryFn: (): Promise<DashboardMetrics> => {
      return unifiedApiService.admin.getDashboardMetrics();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting system health
 */
export const useSystemHealth = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'system', 'health'],
    queryFn: (): Promise<SystemHealth> => {
      return unifiedApiService.admin.getSystemHealth();
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Refetch every minute
  });
};

/**
 * Query hook for getting recent activity
 */
export const useRecentActivity = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'activity'],
    queryFn: (): Promise<ActivityLog[]> => {
      return unifiedApiService.admin.getRecentActivity();
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Query hook for getting admin users
 */
export const useAdminUsers = () => {
  return useQuery({
    queryKey: queryKeys.admin.users(),
    queryFn: (): Promise<AdminUser[]> => {
      return unifiedApiService.admin.getAdminUsers();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting candidates
 */
export const useCandidates = () => {
  return useQuery({
    queryKey: queryKeys.admin.candidates(),
    queryFn: (): Promise<CandidateProfile[]> => {
      return unifiedApiService.admin.getCandidates();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting candidate details
 */
export const useCandidateDetails = (id: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.candidates(), id],
    queryFn: (): Promise<CandidateProfile> => {
      return unifiedApiService.admin.getCandidateDetails(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting system settings
 */
export const useSystemSettings = () => {
  return useQuery({
    queryKey: queryKeys.admin.systemSettings(),
    queryFn: (): Promise<Record<string, string>> => {
      return unifiedApiService.admin.getSystemSettings();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Mutation hook for creating admin user
 */
export const useCreateAdminUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateAdminUserRequest): Promise<AdminUser> => {
      return unifiedApiService.admin.createAdminUser(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
    },
  });
};

/**
 * Mutation hook for updating admin user
 */
export const useUpdateAdminUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdminUserRequest }): Promise<AdminUser> => {
      return unifiedApiService.admin.updateAdminUser(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
    },
  });
};

/**
 * Mutation hook for deleting admin user
 */
export const useDeleteAdminUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await unifiedApiService.admin.deleteAdminUser(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
    },
  });
};

/**
 * Mutation hook for toggling user status
 */
export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string): Promise<AdminUser> => {
      return unifiedApiService.admin.toggleUserStatus(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
    },
  });
};

/**
 * Mutation hook for updating system settings
 */
export const useUpdateSystemSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (request: UpdateSystemSettingsRequest): Promise<void> => {
      await unifiedApiService.admin.updateSystemSettings(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.systemSettings() });
    },
  });
};

/**
 * Mutation hook for exporting jobs
 */
export const useExportJobs = () => {
  return useMutation({
    mutationFn: (request: ExportRequest): Promise<ExportResponse> => {
      return unifiedApiService.admin.exportJobs(request);
    },
  });
};

/**
 * Mutation hook for exporting applications
 */
export const useExportApplications = () => {
  return useMutation({
    mutationFn: (request: ExportRequest): Promise<ExportResponse> => {
      return unifiedApiService.admin.exportApplications(request);
    },
  });
};

/**
 * Mutation hook for exporting candidates
 */
export const useExportCandidates = () => {
  return useMutation({
    mutationFn: (request: ExportRequest): Promise<ExportResponse> => {
      return unifiedApiService.admin.exportCandidates(request);
    },
  });
};

/**
 * Query hook for getting job applications (admin)
 */
export const useJobApplications = (jobId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobs', jobId, 'applications'],
    queryFn: () => {
      return unifiedApiService.admin.getJobApplications(jobId);
    },
    enabled: !!jobId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting application video metadata
 */
export const useApplicationVideo = (applicationId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'applications', applicationId, 'video'],
    queryFn: () => {
      return unifiedApiService.applications.getApplicationVideo(applicationId);
    },
    enabled: !!applicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry if video doesn't exist
  });
};

/**
 * Query hook for checking if application has video
 */
export const useHasApplicationVideo = (applicationId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'applications', applicationId, 'has-video'],
    queryFn: () => {
      return unifiedApiService.applications.hasApplicationVideo(applicationId);
    },
    enabled: !!applicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mutation hook for deleting application video
 */
export const useDeleteApplicationVideo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (applicationId: string): Promise<void> => {
      await unifiedApiService.applications.deleteApplicationVideo(applicationId);
    },
    onSuccess: (_, applicationId) => {
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'applications', applicationId, 'video'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'applications', applicationId, 'has-video'] 
      });
    },
  });
};
